import { useState, useEffect, useCallback, useRef } from 'react'
import { pb } from '@/lib/pocketbase'
import { Collections, type BooksResponse } from '@/types/pocketbase-types'

export interface BookSuggestion {
  id: string
  title: string
  author: string
}

export interface UseBookAutocompleteReturn {
  suggestions: BookSuggestion[]
  isLoading: boolean
  error: string | null
  search: (query: string) => void
  clearSuggestions: () => void
}

export const useBookAutocomplete = (): UseBookAutocompleteReturn => {
  const [suggestions, setSuggestions] = useState<BookSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
    setError(null)
  }, [])

  const search = useCallback(async (query: string) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // If query is empty or too short, clear suggestions
    if (!query || query.trim().length < 2) {
      clearSuggestions()
      return
    }

    // Debounce the search
    const timeout = setTimeout(async () => {
      setIsLoading(true)
      setError(null)

      // Create new abort controller for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const trimmedQuery = query.trim()
        
        // Search for processed books that match title or author
        const response = await pb.collection(Collections.Books).getList(1, 10, {
          filter: `phase = "processed" && (title ~ "${trimmedQuery}" || author ~ "${trimmedQuery}")`,
          sort: '-created',
        }) as { items: BooksResponse[] }

        // Only update if request wasn't aborted
        if (!abortController.signal.aborted) {
          // Format suggestions
          const formattedSuggestions: BookSuggestion[] = response.items.map((book) => ({
            id: book.id,
            title: book.title || book.user_title || 'Unknown Title',
            author: book.author || 'Unknown Author',
          }))

          setSuggestions(formattedSuggestions)
        }
      } catch (err) {
        // Only show error if request wasn't aborted
        if (!abortController.signal.aborted) {
          console.error('Book autocomplete search error:', err)
          setError('Failed to search books')
          setSuggestions([])
        }
      } finally {
        // Only update loading state if request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }, 500) // Increased to 500ms debounce for better UX

    searchTimeoutRef.current = timeout
  }, [clearSuggestions])

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    suggestions,
    isLoading,
    error,
    search,
    clearSuggestions,
  }
}